/**
 * Project Info Tab Component
 * 
 * Displays detailed information about a project
 */

import { useState, useEffect } from 'react'
import {
  Card,
  Descriptions,
  Tag,
  Typography,
  message,
  Spin,
  Space,
  Button,
} from 'antd'
import {
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons'
import api from '@/services/api'

const { Title, Text } = Typography

interface ProjectInfoTabProps {
  projectId: number
}

interface Project {
  id: number
  crm_id: number
  project_name: string
  status: string
  parse_domains: boolean
  parse_phones: boolean
  parse_manual: boolean
  use_mg: boolean
  vdl_autonorms: boolean
  default_limit: number
  max_limit: number
  total_sources: number
  active_sources: number
  inactive_sources: number
  pending_sources: number
  created_at: string
  updated_at: string
  last_sync_at: string | null
  last_collection_run_at: string | null
  config: string | null
}

interface Statistics {
  active_count: number
  inactive_count: number
  pre_approved_count?: number
  total_domains_count?: number
  available_domains_count?: number
  can_generate_config: boolean
}

function ProjectInfoTab({ projectId }: ProjectInfoTabProps) {
  const [project, setProject] = useState<Project | null>(null)
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(false)

  const fetchData = async () => {
    try {
      setLoading(true)
      
      const [projectData, statsData] = await Promise.all([
        api.getProject(projectId),
        api.getProjectStatistics(projectId),
      ])
      
      setProject(projectData)
      setStatistics(statsData)
    } catch (error: any) {
      console.error('Error loading project info:', error)
      message.error('Ошибка загрузки информации о проекте')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [projectId])

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '—'
    try {
      let date: Date
      if (dateString.includes('Z') || dateString.match(/[+-]\d{2}:\d{2}$/)) {
        date = new Date(dateString)
      } else if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
        date = new Date(dateString + 'Z')
      } else {
        date = new Date(dateString)
      }
      
      if (isNaN(date.getTime())) return '—'
      
      return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    } catch (error) {
      console.error('Date formatting error:', error)
      return '—'
    }
  }

  const getProjectStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      active: 'Активен',
      test_active: 'Тест активен',
      pause: 'Пауза',
      stop: 'Остановлен',
      test_stop: 'Тест остановлен',
    }
    return statusMap[status] || status
  }

  const getProjectStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      active: 'success',
      test_active: 'processing',
      pause: 'warning',
      stop: 'default',
      test_stop: 'default',
    }
    return colorMap[status] || 'default'
  }

  if (loading && !project) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin size="large" />
      </div>
    )
  }

  if (!project) {
    return (
      <Card>
        <Text type="secondary">Не удалось загрузить информацию о проекте</Text>
      </Card>
    )
  }

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* * Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={3} style={{ margin: 0 }}>
            {project.project_name}
          </Title>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchData}
            loading={loading}
          >
            Обновить
          </Button>
        </div>

        {/* * Main info */}
        <Card title="Основная информация">
          <Descriptions column={2} bordered>
            <Descriptions.Item label="ID проекта (CRM)">
              {project.crm_id}
            </Descriptions.Item>
            <Descriptions.Item label="Статус">
              <Tag color={getProjectStatusColor(project.status)}>
                {getProjectStatusLabel(project.status)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Создан">
              {formatDate(project.created_at)}
            </Descriptions.Item>
            <Descriptions.Item label="Обновлен">
              {formatDate(project.updated_at)}
            </Descriptions.Item>
            <Descriptions.Item label="Последняя синхронизация">
              {project.last_sync_at ? (
                <Space>
                  <SyncOutlined />
                  <Text>{formatDate(project.last_sync_at)}</Text>
                </Space>
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Последняя синхронизация VDL">
              {project.last_collection_run_at ? formatDate(project.last_collection_run_at) : '—'}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* * Statistics */}
        {statistics && (
          <Card title="Статистика источников">
            <Descriptions column={2} bordered>
              <Descriptions.Item label="Всего источников">
                <Text strong style={{ fontSize: 16 }}>
                  {statistics.total_domains_count ?? project.total_sources}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Доступно для проверки">
                <Text strong style={{ fontSize: 16, color: '#fa8c16' }}>
                  {statistics.available_domains_count ?? project.pending_sources}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Предодобрено">
                <Text strong style={{ fontSize: 16, color: '#1890ff' }}>
                  {statistics.pre_approved_count || 0}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Активных">
                <Text strong style={{ fontSize: 16, color: '#52c41a' }}>
                  {statistics.active_count}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Неактивных">
                <Text strong style={{ fontSize: 16, color: '#8c8c8c' }}>
                  {statistics.inactive_count}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="AI конфиг">
                {statistics.can_generate_config ? (
                  <Tag color="success" icon={<CheckCircleOutlined />}>
                    Доступен для генерации
                  </Tag>
                ) : (
                  <Tag color="default" icon={<CloseCircleOutlined />}>
                    Недостаточно данных
                  </Tag>
                )}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}

        {/* * Parsing settings */}
        <Card title="Настройки парсинга">
          <Descriptions column={2} bordered>
            <Descriptions.Item label="Парсинг доменов">
              {project.parse_domains ? (
                <Tag color="success" icon={<CheckCircleOutlined />}>Включен</Tag>
              ) : (
                <Tag color="default" icon={<CloseCircleOutlined />}>Выключен</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Парсинг телефонов">
              {project.parse_phones ? (
                <Tag color="success" icon={<CheckCircleOutlined />}>Включен</Tag>
              ) : (
                <Tag color="default" icon={<CloseCircleOutlined />}>Выключен</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Ручной парсинг (Вебмастер)">
              {project.parse_manual ? (
                <Tag color="success" icon={<CheckCircleOutlined />}>Включен</Tag>
              ) : (
                <Tag color="default" icon={<CloseCircleOutlined />}>Выключен</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Использование MG">
              {project.use_mg ? (
                <Tag color="success" icon={<CheckCircleOutlined />}>Включен</Tag>
              ) : (
                <Tag color="default" icon={<CloseCircleOutlined />}>Выключен</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="VDL автонормы">
              {project.vdl_autonorms ? (
                <Tag color="processing" icon={<CheckCircleOutlined />}>Включено</Tag>
              ) : (
                <Tag color="default" icon={<CloseCircleOutlined />}>Выключено</Tag>
              )}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* * Limits */}
        <Card title="Лимиты">
          <Descriptions column={2} bordered>
            <Descriptions.Item label="Лимит по умолчанию">
              {project.default_limit > 0 ? project.default_limit : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Максимальный лимит">
              {project.max_limit > 0 ? project.max_limit : '—'}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* * Config */}
        {project.config && (
          <Card title="Конфигурация">
            <pre style={{ 
              backgroundColor: '#f5f5f5', 
              padding: 16, 
              borderRadius: 4,
              overflowX: 'auto',
              fontSize: 12,
            }}>
              {JSON.stringify(JSON.parse(project.config), null, 2)}
            </pre>
          </Card>
        )}
      </Space>
    </div>
  )
}

export default ProjectInfoTab

